import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { WhatsAppCTA } from "../WhatsAppCTA";
import { IMAGES } from "../../lib/site";
import { fadeUp, viewport } from "../../lib/motion";

const FEATURES = [
  {
    id: "servicos",
    image: IMAGES.tela,
    alt: "Tela de celular",
    title: "Tela quebrada?",
    description: "Problemas na tela, vidro quebrado ou display danificado.",
  },
  {
    image: IMAGES.aparelho,
    alt: "Aparelhos diversos",
    title: "Celular, tablet ou outro aparelho?",
    description: "Conte o que aconteceu. A Inove Tech avalia se podemos ajudar.",
  },
  {
    image: IMAGES.bateria,
    alt: "Bateria de aparelho",
    title: "Bateria acabando rápido?",
    description: "Quando a bateria dura pouco, desliga sozinha ou apresenta problemas.",
  },
  {
    image: IMAGES.placa,
    alt: "Placa interna de aparelho",
    title: "Problemas internos?",
    description: "Quando o aparelho apresenta falhas que exigem uma avaliação mais detalhada.",
  },
];

const FeatureRow = ({ feature, index }) => {
  const ref = useRef(null);
  const reversed = index % 2 === 1;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <div
      ref={ref}
      id={feature.id}
      data-testid={`feature-${index + 1}`}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center ${
        index === 0 ? "" : "mt-28 md:mt-40"
      }`}
    >
      {/* Image */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={viewport}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={`relative ${reversed ? "lg:order-2" : "lg:order-1"}`}
      >
        <div className="relative rounded-[28px] overflow-hidden border border-white/[0.06]">
          <motion.img
            style={{ y }}
            src={feature.image}
            alt={feature.alt}
            loading="lazy"
            className="w-full h-[320px] md:h-[460px] object-cover scale-110"
          />
          <div className="absolute inset-0 shadow-[inset_0_0_120px_rgba(0,0,0,0.6)]" />
        </div>
      </motion.div>

      {/* Text */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        className={`${reversed ? "lg:order-1" : "lg:order-2"}`}
      >
        <h3 className="text-3xl md:text-5xl font-bold tracking-tighter leading-[1.02] text-[#f5f5f7]">
          {feature.title}
        </h3>
        <p className="mt-5 text-base md:text-lg text-[#86868b] max-w-md">
          {feature.description}
        </p>
        <div className="mt-8">
          <WhatsAppCTA
            label="Falar no WhatsApp"
            variant="secondary"
            icon={<MessageCircle size={18} />}
            testId={`feature-${index + 1}-whatsapp`}
          />
        </div>
      </motion.div>
    </div>
  );
};

export const Features = () => {
  return (
    <section
      data-testid="features-section"
      className="relative bg-[#000000] py-28 md:py-40"
    >
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <div className="relative w-full max-w-7xl mx-auto px-6 md:px-12">
        {FEATURES.map((feature, index) => (
          <FeatureRow key={index} feature={feature} index={index} />
        ))}
      </div>
    </section>
  );
};
